import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import iconClose from '../../../assets/svg/icon-cross.svg';
import iconZip from '../../../assets/svg/icon-zip.svg';
import iconKlarna from '../../../assets/svg/icon-klarna.svg';
import iconAfterpay from '../../../assets/svg/icon-afterpay.svg';
import iconPaypal from '../../../assets/svg/icon-paypal.svg';
// eslint-disable-next-line @shopify/strict-component-boundaries
import initLoginModal from '../../../assets/scripts/components/login-modal';

import CartItem from './cart-item';
import FreeShippingBar from './free-shipping-bar';
import CustomMessage from './custom-message';
import GiftWithPurchase, {checkGiftWithPurchaseStatus} from './gift-with-purchase';
import CartUpsells from './cart-upsells/CartUpsells';

const emptyCart = {
  item_count: 0,
  items: [],
  total_price: 0,
};

const Cart = ({rootUrl, freeShippingBarSettings, isCartPage, cartMessage}) => {
  const defaultCart = window.theme.cart ? window.theme.cart : emptyCart;

  const [cart, setCart] = useState(defaultCart);
  const [customCartData, setCustomCartData] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gwpManuallyRemoved, setGwpManuallyRemoved] = useState(false);

  const itemsCount = cart.item_count;
  const cartItems = cart.items;
  const originalTotal = cart.original_total_price;
  const total = cart.total_price;

  const cartLoadingMessage = window.theme.strings?.loadingMessage || 'Updating, please wait';

  const [upsellProducts, setUpsellProducts] = useState([]);
  const upsellsEnabled = window.theme.cartUpsells ? window.theme.cartUpsells.enabled : false;
  const upsellsLimit = window.theme.cartUpsells ? window.theme.cartUpsells.limit : 1;

  const getUpsellProducts = async (items) => {
    const _items = items ? items : cartItems;
    if (_items.length <= 0) {
      return;
    }

    // Get upsell products using the product recommendations API
    const mostExpensiveProduct = _items.reduce((prev, current) =>
      prev.final_price > current.final_price ? prev : current
    );
    const productHandle = mostExpensiveProduct.handle;

    // First check sessionStorage to see if we already have this product
    let upsellsRecommendations = sessionStorage.getItem(`__recommendations:${productHandle}`);
    if (upsellsRecommendations) {
      upsellsRecommendations = JSON.parse(upsellsRecommendations);
    } else {
      upsellsRecommendations = [];
      const productId = mostExpensiveProduct.product_id;
      const url = `${rootUrl}/recommendations/products.json?product_id=${productId}&limit=${upsellsLimit + 4}`;
      const options = {
        headers: {'Content-Type': 'application/json'},
      };
      try {
        const response = await fetch(url, options);
        const json = await response.json();
        upsellsRecommendations = json.products;
        sessionStorage.setItem(`__recommendations:${productHandle}`, JSON.stringify(upsellsRecommendations));
      } catch (err) {
        window.console.error(err);
      }
    }

    // Remove any products already in the cart or in the upsells section
    upsellsRecommendations = upsellsRecommendations.filter(({handle}) => {
      const isInCart = _items.some((item) => item.handle === handle);

      return !isInCart;
    });

    setUpsellProducts(upsellsRecommendations.slice(0, upsellsLimit));
  };

  const gwpItem = JSON.parse(document.querySelector('[data-gift-with-purchase]')?.innerHTML ?? '{}');
  const gwpStatus = checkGiftWithPurchaseStatus(gwpItem, cartItems);
  const checkGwpManuallyRemoved = () => sessionStorage.getItem('gwpManuallyRemoved') !== null;
  setGwpManuallyRemoved(checkGwpManuallyRemoved());

  const nonGwpDiscountTotal =
    cart.total_discount > 0
      ? cartItems.reduce((discountTotal, item) => {
          if (item.properties?._gwp) return discountTotal;

          return discountTotal + (item.total_discount || 0);
        }, 0)
      : 0;

  const fetchCart = async () => {
    const url = `${rootUrl}/cart.js`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  const fetchCustomCartData = async () => {
    const url = `${rootUrl}/cart?view=custom-data`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch customCart response was not OK');
      }
      const json = await response.json();
      setCustomCartData(json);
    } catch (err) {
      window.console.error(err);
    }
  };

  const updateCart = async (newCart) => {
    setCart(newCart);
    await fetchCustomCartData();
    const counters = document.querySelectorAll('[data-items-count]');
    counters.forEach((counter) => (counter.innerHTML = newCart.item_count));
    setIsLoading(false);

    if (upsellsEnabled) {
      getUpsellProducts(newCart.items);
    }

    // Klaviyo tracking for abandoned cart
    if (window._learnq) {
      let cartTracking = {
        total_price: newCart.total_price / 100,
        $value: newCart.total_price / 100,
        total_discount: newCart.total_discount,
        original_total_price: newCart.original_total_price / 100,
        items: newCart.items,
      };
      if (typeof window.item !== 'undefined') {
        cartTracking = Object.assign(newCart, window.item);
      }
      window._learnq.push(['track', 'Added to Cart', cartTracking]);
    }
  };

  /**
   *
   * @typedef {Object} Item
   * @property {number} id - The Shopify variant ID.
   * @property {number} quantity - The quantity to add of this variant.
   * @property {Object.<string, string>} [properties] - Optional line item properties.
   * @property {Object} [variant] - Optional variant.
   */

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   */
  const addLineItems = async (items) => {
    const data = {
      items: [items].flat(),
    };
    const url = `${rootUrl}/cart/add.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      setIsLoading(true);
      const response = await fetch(url, options);
      if (response.ok === false) {
        const errorResponse = await response.json();
        setErrorMessage(errorResponse.description);
        setIsLoading(false);
      }
      const cartContents = await fetchCart();
      updateCart(cartContents);
    } catch (err) {
      setIsLoading(false);
      window.console.error(err);
    }
  };

  const updateLineItems = async (updates) => {
    const data = {
      updates,
    };
    const url = `${rootUrl}/cart/update.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      setIsLoading(true);
      const response = await fetch(url, options);
      if (response.ok === false) {
        const errorResponse = await response.json();
        setErrorMessage(errorResponse.description);
        setIsLoading(false);
      }
      const cartContents = await response.json();
      updateCart(cartContents);
    } catch (err) {
      setIsLoading(false);
      window.console.error(err);
    }
  };

  const closeCart = () => {
    const cartModal = document.querySelector('[data-modal-cart]');
    document.body.classList.remove('side-cart-is-visible');
    if (cartModal) {
      cartModal.setAttribute('aria-hidden', true);
    }
    const cartOpenButton = document.querySelector('[data-toggle-cart]');
    if (cartOpenButton) {
      cartOpenButton.focus();
    }
  };

  const handleCartToggle = (event) => {
    if (event) {
      event.preventDefault();
    }

    const cartModal = document.querySelector('[data-modal-cart]');

    if (document.body.classList.contains('side-cart-is-visible')) {
      closeCart();
    } else {
      document.body.classList.add('side-cart-is-visible');

      if (cartModal) {
        cartModal.setAttribute('aria-hidden', false);
      }

      const searchModalActive = document.querySelector('.modal--search');
      if (searchModalActive) {
        const toggleSearch = new Event('toggleSearch');
        document.dispatchEvent(toggleSearch);
      }
    }
  };

  /**
   *
   * @param {Item[]} items - Array of {@link Item} objects to add to cart.
   * @param {boolean} openCart - Triggers the cart to open when item added if true
   */

  const handleAddToCartEvent = async (items, openCart = false, successCallback) => {
    // Items could contain extra data like properties or variant info.
    // This reassignment strips out all but id, qty and properties to pass
    // to the addLineItems function for the cart.
    const lineItems = [items].flat().map((item) => {
      // ID and Qty will always be part of the array
      const newLineItem = {
        id: item.id,
        quantity: item.quantity,
      };
      // Only add properties if they exist
      if ('properties' in item) {
        newLineItem.properties = item.properties;
      }
      return newLineItem;
    });
    try {
      await addLineItems(lineItems);
      if (openCart && !isCartPage) {
        handleCartToggle();
      }

      if (successCallback) {
        successCallback();
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  const cloneHeaderLinks = () => {
    if (isCartPage) return;
    const headerGridRight = document.querySelector('.header__grid-right');
    if (!headerGridRight) return;

    const cloneHeaderItem = headerGridRight.cloneNode(true);
    const cartHeader = document.querySelector('.cart__header');
    cartHeader.appendChild(cloneHeaderItem);
    initLoginModal();

    const headerLinks = cloneHeaderItem.querySelectorAll('.nav-secondary__link');
    headerLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (link.hasAttribute('data-toggle-cart')) {
          event.preventDefault();
        }
        closeCart();
        if (link.classList.contains('search-modal__trigger')) {
          const toggleSearch = new Event('toggleSearch');
          document.dispatchEvent(toggleSearch);
        }
      });
    });
  };

  useEffect(async () => {
    window.sideCart = {
      handleAddToCartEvent,
    };

    await fetchCustomCartData();

    const cartToggles = document.querySelectorAll('[data-toggle-cart]');

    cartToggles.forEach((cartToggle) => {
      cartToggle.addEventListener('click', (event) => {
        event.preventDefault();

        const headerSection = document.querySelector('.header-section');
        if (document.body.classList.contains('has-header-bottom') && headerSection.offsetTop > 0) {
          window.scrollTo({
            top: headerSection.offsetTop + 1,
          });

          setTimeout(() => {
            handleCartToggle(event);
          }, 400);

          return;
        }

        handleCartToggle(event);
      });
    });

    // Load upsells if enabled
    if (upsellsEnabled) {
      getUpsellProducts();
    }

    cloneHeaderLinks();
  }, []);

  useEffect(() => {
    // Handle Gift with Purchase logic
    if (!gwpStatus) return;

    const gwpVariantId = gwpItem.currentVariant?.id;
    if (!gwpVariantId) return;

    if (gwpStatus.isEligible) {
      // Add GWP to cart if eligible and not already present
      if (gwpStatus.isInCart || gwpManuallyRemoved) return;

      handleAddToCartEvent([{id: gwpVariantId, quantity: 1, properties: {_gwp: 'true'}}], false);
    } else {
      // Remove GWP from cart if not eligible
      if (!gwpStatus.isInCart) return;

      const gwpCartItem = cartItems.find((item) => item.id === gwpVariantId && item.properties?._gwp);
      if (gwpCartItem) {
        updateLineItems({[gwpCartItem.key]: 0});
      }
    }
  }, [cart]);

  return (
    <>
      <div onMouseLeave={closeCart}>
        {!isCartPage && (
          <div className="cart__header">
            <div className="cart__items-total">
              {window.theme.strings.cart} ({itemsCount})
            </div>
            <button
              type="button"
              className="cart__close btn-none"
              aria-label={window.theme.strings.closeCart}
              data-toggle-cart
            >
              <span dangerouslySetInnerHTML={{__html: iconClose}} />
            </button>
          </div>
        )}
        <div className={`cart__loading ${isLoading ? 'is-shown' : ''}`}>
          <div className="cart__loader" />
          <p>
            <strong>{cartLoadingMessage}</strong>
          </p>
        </div>
        {isCartPage ? (
          <div className="page-header">
            <h1>{window.theme.strings.cartTitle}</h1>
          </div>
        ) : null}
        {freeShippingBarSettings.enabled && (
          <FreeShippingBar total={total} freeShippingBarSettings={freeShippingBarSettings} isCartPage={isCartPage} />
        )}
        {cartMessage?.enabled && cartMessage?.customMessage && <CustomMessage cartMessage={cartMessage} />}
        {errorMessage && (
          <div className="cart__error-message form__error-message">
            {errorMessage}
            <button
              type="button"
              className="cart__dismiss-message btn-none"
              aria-label={window.theme.strings.dismissErrorMessage}
              onClick={() => setErrorMessage(null)}
            >
              <span dangerouslySetInnerHTML={{__html: iconClose}} />
            </button>
          </div>
        )}
        <div className={cartItems.length ? 'cart__content-container' : 'cart__content-container empty'}>
          <div className={isCartPage ? 'cart__content container container--md' : 'cart__content'}>
            <ul className="cart__cart-items-container list-none">
              {cartItems.length ? (
                cartItems
                  .filter((item) => !item.properties?._gwp)
                  .map((item) => {
                    const customData =
                      customCartData?.items?.find((customCartItem) => customCartItem.id === item.id) ?? {};
                    return (
                      <CartItem item={item} customData={customData} updateLineItems={updateLineItems} key={item.key} />
                    );
                  })
              ) : (
                <li className="cart__empty">
                  <h3>{window.theme.strings.cartEmpty}</h3>
                </li>
              )}
            </ul>
            {gwpStatus && cartItems.length > 0 && (
              <GiftWithPurchase
                item={gwpItem}
                gwpStatus={gwpStatus}
                cartItems={cartItems}
                setGwpManuallyRemoved={setGwpManuallyRemoved}
                addToCart={handleAddToCartEvent}
                updateLineItems={updateLineItems}
              />
            )}

            {isCartPage ? (
              <div className="cart__summary">
                <h3 className="cart__summary-header">{window.theme.strings.cartSummaryHeading}</h3>
                <div className="cart__summary-item">
                  <span>{window.theme.strings.subtotal}</span>
                  <span>{formatMoney(originalTotal, window.theme.moneyFormat)}</span>
                </div>

                {nonGwpDiscountTotal > 0 && (
                  <div className="cart__summary-item">
                    <span>{window.theme.strings.discount}</span>
                    <span>{`-${formatMoney(cart.total_discount, window.theme.moneyFormat)}`}</span>
                  </div>
                )}

                <div className="cart__summary-item">
                  <span>{window.theme.strings.cartTotal}</span>
                  <span>{formatMoney(total, window.theme.moneyFormat)}</span>
                </div>
                <div className="cart__checkout">
                  <a href={`${rootUrl}/checkout`} className="btn btn--block">
                    {window.theme.strings.checkout}
                  </a>
                </div>
              </div>
            ) : null}
          </div>

          {cartItems.length > 0 && !isCartPage && (
            <div className="cart__totals">
              <div className="cart__subtotals">
                {cart.cart_level_discount_applications &&
                  cart.cart_level_discount_applications.map((cartDiscount) => (
                    <div className="cart__subtotal" key={cartDiscount}>
                      <span className="cart__subtotal-discount">
                        {window.theme.strings.discount}
                        {cartDiscount.title}
                      </span>
                      <span className="cart__subtotal-value">
                        -{formatMoney(cartDiscount.total_allocated_amount, window.theme.moneyFormat)}
                      </span>
                    </div>
                  ))}
                {!cart.cart_level_discount_applications.length && nonGwpDiscountTotal > 0 && (
                  <div className="cart__subtotal">
                    <span className="cart__subtotal-discount">{window.theme.strings.totalDiscounts}:</span>
                    <span className="cart__subtotal-value">
                      {formatMoney(nonGwpDiscountTotal, window.theme.moneyFormat)}
                    </span>
                  </div>
                )}

                <div className="cart__subtotal">
                  <span className="cart__subtotal-label">{window.theme.strings.subtotal}:</span>
                  <span className="cart__subtotal-value">{formatMoney(total, window.theme.moneyFormat)}</span>
                </div>
              </div>
              <div className="cart__checkout">
                <a href={`${rootUrl}/checkout`} className="btn">
                  {window.theme.strings.checkout}
                </a>
              </div>
            </div>
          )}

          {upsellsEnabled && upsellProducts.length > 0 && (
            <CartUpsells
              total={total}
              products={upsellProducts}
              addToCart={handleAddToCartEvent}
              freeShippingBarSettings={freeShippingBarSettings}
            />
          )}

          {/* Payment Method Logos */}
          {cartItems.length > 0 && (
            <div className="cart__payment-methods">
              {window.theme.cartPaymentZipEnabled && <span dangerouslySetInnerHTML={{__html: iconZip}} />}
              {window.theme.cartPaymentAfterpayEnabled && <span dangerouslySetInnerHTML={{__html: iconAfterpay}} />}
              {window.theme.cartPaymentPaypalEnabled && <span dangerouslySetInnerHTML={{__html: iconPaypal}} />}
              {window.theme.cartPaymentKlarnaEnabled && <span dangerouslySetInnerHTML={{__html: iconKlarna}} />}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

Cart.propTypes = {
  rootUrl: PropTypes.string,
  freeShippingBarSettings: PropTypes.func,
  isCartPage: PropTypes.bool,
  cartMessage: PropTypes.func,
};

export default Cart;
